/**
 * ColumnFilter Component
 * 
 * A reusable, framework-agnostic column filter component supporting multiple filter types.
 * 
 * @component
 * @example
 * ```tsx
 * <ColumnFilter
 *   columnKey="status"
 *   columnLabel="Status"
 *   filterType="select"
 *   value={filterValue}
 *   onFilterChange={(value) => setFilterValue(value)}
 *   selectOptions={[
 *     { value: "active", label: "Active" },
 *     { value: "inactive", label: "Inactive" }
 *   ]}
 * />
 * ```
 * 
 * @props
 * - columnKey: string - Unique identifier for the column
 * - columnLabel: string - Display label for the column
 * - filterType: "text" | "number" | "date" | "select" - Type of filter to display
 * - value: string - Current filter value
 * - onFilterChange: (value: string) => void - Callback when filter changes
 * - selectOptions?: Array<{value: string, label: string}> - Options for select filter type
 * 
 * @performance
 * - Optimized with useCallback for event handlers
 * - Minimal re-renders through controlled state
 * - Efficient filter application logic
 */

import { useState, useEffect, useCallback } from 'react';
import { ListFilter, X } from 'lucide-react';

import { Button } from '@/components/evo/ui/button';
import { Input } from '@/components/evo/ui/input';
import { Label } from '@/components/evo/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/evo/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/evo/ui/select';
import { CustomDatePicker } from '@/components/evo/ui/custom-date-picker';
import { Checkbox } from '@/components/evo/ui/checkbox';

type FilterType = 'text' | 'number' | 'date' | 'select';
type FilterCondition =
  | 'contains' | 'equals' | 'startsWith' | 'endsWith'
  | 'greaterThan' | 'lessThan' | 'between' | 'before' | 'after' | 'range';

interface ColumnFilterProps {
  columnKey: string;
  columnLabel: string;
  filterType: FilterType;
  value: string;
  onFilterChange: (value: string) => void;
  selectOptions?: Array<{ value: string; label: string }>;
}

// Default condition mappings
const DEFAULT_CONDITIONS: Record<FilterType, FilterCondition> = {
  text: 'contains',
  number: 'equals',
  date: 'equals',
  select: 'contains',
};

// Condition options by filter type
const CONDITION_OPTIONS: Record<FilterType, Array<{ value: FilterCondition; label: string }>> = {
  text: [
    { value: 'contains', label: 'Contains' },
    { value: 'equals', label: 'Equals' },
    { value: 'startsWith', label: 'Starts with' },
    { value: 'endsWith', label: 'Ends with' },
  ],
  number: [
    { value: 'equals', label: 'Equals' },
    { value: 'greaterThan', label: 'Greater than' },
    { value: 'lessThan', label: 'Less than' },
    { value: 'between', label: 'Between' },
  ],
  date: [
    { value: 'equals', label: 'Equals' },
    { value: 'before', label: 'Before' },
    { value: 'after', label: 'After' },
    { value: 'range', label: 'Range' },
  ],
  select: [],
};

export function ColumnFilter({
  columnKey,
  columnLabel,
  filterType,
  value,
  onFilterChange,
  selectOptions,
}: ColumnFilterProps) {
  // State: Popover visibility
  const [open, setOpen] = useState(false);
  
  // State: Filter condition
  const [condition, setCondition] = useState<FilterCondition>(DEFAULT_CONDITIONS[filterType]);
  
  // State: Filter values - dateValue/dateValue2 stored as YYYY-MM-DD strings for CustomDatePicker
  const [filterValue, setFilterValue] = useState('');
  const [filterValue2, setFilterValue2] = useState('');
  const [dateValue, setDateValue] = useState<string>('');
  const [dateValue2, setDateValue2] = useState<string>('');
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

  // Initialize selected options from value for multi-select
  useEffect(() => {
    if (filterType === 'select' && value) {
      const parts = value.split('|');
      if (parts.length > 1 && parts[parts.length - 1] === 'multiselect') {
        setSelectedOptions(parts.slice(0, -1));
      } else {
        setFilterValue(value);
      }
    }
  }, [value, filterType]);

  // Initialize date values from value prop for date filters (state persistence)
  useEffect(() => {
    if (filterType === 'date') {
      if (!value) {
        setDateValue('');
        setDateValue2('');
        setCondition(DEFAULT_CONDITIONS.date);
        return;
      }

      const parts = value.split('|');
      if (parts.length < 2) {
        return;
      }

      const [value1, value2OrCondition, maybeCondition] = parts;
      const actualCondition = (maybeCondition || value2OrCondition) as FilterCondition;

      setCondition(actualCondition);

      if (actualCondition === 'range') {
        setDateValue(value1 || '');
        setDateValue2(value2OrCondition || '');
      } else {
        setDateValue(value1 || '');
        setDateValue2('');
      }
    }
  }, [value, filterType]);

  const hasActiveFilter = value !== '';

  // Optimized handlers with useCallback
  const handleConditionChange = useCallback((newCondition: string) => {
    setCondition(newCondition as FilterCondition);
  }, []);

  const handleFilterValueChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue(e.target.value);
  }, []);

  const handleFilterValue2Change = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue2(e.target.value);
  }, []);

  const handleDateChange = useCallback((date: string) => {
    setDateValue(date);
  }, []);

  const handleDate2Change = useCallback((date: string) => {
    setDateValue2(date);
  }, []);

  const toggleOption = useCallback((optionValue: string) => {
    setSelectedOptions((prev) =>
      prev.includes(optionValue)
        ? prev.filter((v) => v !== optionValue)
        : [...prev, optionValue]
    );
  }, []);

  const applyFilter = useCallback(() => {
    if (filterType === 'select' && selectOptions) {
      // Multi-select: format as "value1|value2|value3|multiselect"
      if (selectedOptions.length > 0) {
        onFilterChange(`${selectedOptions.join('|')}|multiselect`);
      } else {
        onFilterChange('');
      }
    } else if (filterType === 'date') {
      if (condition === 'range' && dateValue && dateValue2) {
        // Format: "YYYY-MM-DD|YYYY-MM-DD|range"
        onFilterChange(`${dateValue}|${dateValue2}|range`);
      } else if (dateValue) {
        // Format: "YYYY-MM-DD|condition"
        onFilterChange(`${dateValue}|${condition}`);
      }
    } else if (filterType === 'number' && condition === 'between') {
      // Format: "value1|value2|between"
      onFilterChange(`${filterValue}|${filterValue2}|between`);
    } else {
      // Format: "value|condition"
      onFilterChange(`${filterValue}|${condition}`);
    }
    setOpen(false);
  }, [
    filterType, selectOptions, selectedOptions, dateValue, dateValue2,
    filterValue, filterValue2, condition, onFilterChange,
  ]);

  const clearFilter = useCallback(() => {
    setFilterValue('');
    setFilterValue2('');
    setDateValue('');
    setDateValue2('');
    setSelectedOptions([]);
    onFilterChange('');
    setOpen(false);
  }, [onFilterChange]);

  const handleOpenChange = useCallback((newOpen: boolean) => {
    setOpen(newOpen);
  }, []);

  // Check if apply button should be disabled
  const isApplyDisabled = 
    filterType === 'select'
      ? selectedOptions.length === 0
      : filterType === 'date'
        ? !dateValue || (condition === 'range' && !dateValue2)
        : !filterValue || (condition === 'between' && !filterValue2);

  // Render filter input based on type
  const renderFilterInput = () => {
    if (filterType === 'select' && selectOptions) {
      return (
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Select Values</Label>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {selectOptions.map((option) => (
              <div key={option.value} className="flex items-center space-x-2">
                <Checkbox
                  id={`${columnKey}-${option.value}`}
                  checked={selectedOptions.includes(option.value)}
                  onCheckedChange={() => toggleOption(option.value)}
                />
                <label
                  htmlFor={`${columnKey}-${option.value}`}
                  className="text-xs font-normal leading-none cursor-pointer
                    peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (filterType === 'date') {
      return (
        <div className="space-y-2">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">
              {condition === 'range' ? 'From Date' : 'Date'}
            </Label>
            <CustomDatePicker
              value={dateValue}
              onChange={handleDateChange}
              placeholder="Pick a date"
              allowManualInput={true}
              showValidationError={true}
            />
          </div>
          {condition === 'range' && (
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">To Date</Label>
              <CustomDatePicker
                value={dateValue2}
                onChange={handleDate2Change}
                placeholder="Pick a date"
                allowManualInput={true}
                showValidationError={true}
              />
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <div className="space-y-1.5">
          <Label htmlFor={`filter-${columnKey}`} className="text-xs font-medium">
            Value
          </Label>
          <Input
            id={`filter-${columnKey}`}
            type={filterType === 'number' ? 'number' : 'text'}
            placeholder={`Enter ${filterType}...`}
            value={filterValue}
            onChange={handleFilterValueChange}
            className="h-8 text-xs"
          />
        </div>
        {condition === 'between' && (
          <div className="space-y-1.5">
            <Label htmlFor={`filter-${columnKey}-2`} className="text-xs font-medium">
              To Value
            </Label>
            <Input
              id={`filter-${columnKey}-2`}
              type="number"
              placeholder="Enter value..."
              value={filterValue2}
              onChange={handleFilterValue2Change}
              className="h-8 text-xs"
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`h-6 w-6 p-0 ${hasActiveFilter ? 'text-blue-600' : 'text-muted-foreground'}`}
          title={`Filter ${columnLabel}`}
        >
          <ListFilter className="h-4 w-4 stroke-[2]" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="start">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-sm">Filter: {columnLabel}</h4>
            {hasActiveFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilter}
                className="h-6 px-2 text-xs"
              >
                <X className="h-3 w-3 mr-1" />
                Clear
              </Button>
            )}
          </div>

          {filterType !== 'select' && (
            <div className="space-y-1.5">
              <Label htmlFor={`condition-${columnKey}`} className="text-xs font-medium">
                Condition
              </Label>
              <Select
                value={condition}
                onValueChange={handleConditionChange}
              >
                <SelectTrigger id={`condition-${columnKey}`} className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONDITION_OPTIONS[filterType].map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {renderFilterInput()}

          <div className="flex gap-2 pt-1">
            <Button
              onClick={applyFilter}
              className="flex-1 h-8 text-xs"
              disabled={isApplyDisabled}
            >
              Apply
            </Button>
            <Button
              variant="outline"
              onClick={handleOpenChange.bind(null, false)}
              className="flex-1 h-8 text-xs"
            >
              Cancel
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
